import React, { useState, useEffect } from 'react';
import TollgateModal from './TollgateModal';
import { ChevronDown, ChevronRight, Check, Clock, User } from 'lucide-react';
const PHASE_COLORS = {"New Lead":"#94a3b8","Lead":"#60a5fa","Document Prep":"#818cf8","Listing Prep":"#a78bfa","MLS":"#f472b6","Active":"#fb923c","Active Showing":"#818cf8","Offer Prep":"#f472b6","Under Contract":"#facc15","Pre-close":"#a3e635","Pre-Close":"#a3e635","Closed":"#75BB2E"};
const getPhaseColor = (theme) => PHASE_COLORS[theme] || '#071D39';

const themeOrder = [
  "New Lead", "Lead", "Document Prep", "Listing Prep", "MLS", "Active",
  "Active Showing", "Offer Prep",
  "Under Contract", "Pre-close", "Pre-Close", "Closed"
];

const getInitials = (name) => {
  if (!name) return '';
  const parts = name.split(' ');
  return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : parts[0][0].toUpperCase();
};

const getUserColor = (name) => {
  if (!name) return '#516469';
  const colors = ['#75BB2E', '#75BB2E', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#EF4444', '#6366F1'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const StatusIcon = ({ status, onClick }) => {
  const handleClick = (e) => {
    e.stopPropagation();
    onClick && onClick();
  };
  if (status === 'complete') {
    return (
      <div onClick={handleClick} style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: '#75BB2E', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        <Check size={12} color="white" />
      </div>
    );
  }
  if (status === 'in-progress') {
    return (
      <div onClick={handleClick} style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: '#F59E0B', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        <Clock size={12} color="white" />
      </div>
    );
  }
  return <div onClick={handleClick} style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #CBD5E1', backgroundColor: 'white', cursor: 'pointer' }} />;
};

const ProgressBar = ({ completed, total }) => {
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 160 }}>
      <div style={{ flex: 1, height: 8, backgroundColor: '#E2E8F0', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          backgroundColor: '#75BB2E',
          borderRadius: 4,
          transition: 'width 0.4s ease'
        }} />
      </div>
      <span style={{ fontSize: 13, color: '#516469', fontWeight: 600, whiteSpace: 'nowrap' }}>{completed}/{total}</span>
    </div>
  );
};

const ThemeSection = ({ theme, groupNum, tasks, isOpen, onToggle, onTaskClick, onToggleStatus, onToggleAll, isTaskUrgent, isMobile, phaseColor }) => {
  const completed = tasks.filter(t => t.status === 'complete').length;
  const inProgress = tasks.filter(t => t.status === 'in-progress').length;
  const total = tasks.length;
  const allDone = completed === total && total > 0;

  return (
    <div style={{ marginBottom: 2 }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          padding: '14px 20px',
          backgroundColor: phaseColor,
          border: '1px solid',
          borderColor: phaseColor,
          borderRadius: isOpen ? '10px 10px 0 0' : 10,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          gap: 14
        }}
      >
        <div style={{ color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center' }}>
          {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </div>

        <div style={{
          width: 28, height: 28, borderRadius: 6,
          backgroundColor: isOpen ? 'rgba(255,255,255,0.25)' : allDone ? '#75BB2E' : '#F1F5F9',
          color: isOpen ? '#FFFFFF' : allDone ? '#FFFFFF' : '#516469',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 700
        }}>
          {groupNum}
        </div>

        <span style={{
          fontWeight: 700,
          fontSize: 15,
          color: '#FFFFFF',
          flex: 1,
          textAlign: 'left',
          letterSpacing: '-0.01em'
        }}>
          {theme}
        </span>

        {inProgress > 0 && (
          <span style={{
            fontSize: 11, fontWeight: 600,
            padding: '2px 8px', borderRadius: 12,
            backgroundColor: isOpen ? 'rgba(245,158,11,0.2)' : '#FEF3C7',
            color: '#92400E'
          }}>
            {inProgress} in progress
          </span>
        )}

        <ProgressBar completed={completed} total={total} />
      </button>

      {isOpen && (
        <div style={{
          border: '1px solid #E2E8F0',
          borderTop: 'none',
          borderRadius: '0 0 10px 10px',
          overflow: 'hidden',
          backgroundColor: `${phaseColor}10`,
        }}>
          {/* Column headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '32px 1fr 28px 70px' : '44px 1fr 100px 90px 90px',
            padding: isMobile ? '6px 12px' : '8px 20px',
            borderBottom: '1px solid #E2E8F0',
            backgroundColor: '#F8FAFC',
            alignItems: 'center',
          }}>
            <input
              type="checkbox"
              checked={allDone}
              onChange={(e) => { e.stopPropagation(); onToggleAll(allDone); }}
              onClick={(e) => e.stopPropagation()}
              title={allDone ? 'Uncheck all' : 'Check all complete'}
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#75BB2E' }}
            />
            <span style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Task</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}></span>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>Status</span>
            {!isMobile && <span style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Due Date</span>}
          </div>

          {tasks.map((task, i) => {
            const urgent = isTaskUrgent ? isTaskUrgent(task) : false;
            return (
              <div
                key={task.id || i}
                onClick={() => onTaskClick(task)}
                className="accordion-task-row"
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '28px 1fr 28px 64px' : '44px 1fr 100px 90px 90px',
                  padding: isMobile ? '10px 12px' : '12px 20px',
                  borderBottom: i < tasks.length - 1 ? '1px solid #F1F5F9' : 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  backgroundColor: task.status === 'complete' ? '#FAFFF9' : urgent ? '#FEF2F2' : 'transparent',
                  alignItems: 'center'
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#F1F5F9'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = task.status === 'complete' ? '#FAFFF9' : urgent ? '#FEF2F2' : 'transparent'; }}
              >
                <StatusIcon status={task.status} onClick={() => onToggleStatus(task)} />

                <div style={{ paddingRight: 12, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                  <span style={{
                    fontSize: 14,
                    color: task.status === 'complete' ? '#94A3B8' : '#1E293B',
                    textDecoration: task.status === 'complete' ? 'line-through' : 'none',
                    fontWeight: task.status === 'in-progress' ? 600 : 400
                  }}>
                    {task.name}
                  </span>
                  {urgent && task.status !== 'complete' && (() => {
                    const today = new Date(); today.setHours(0,0,0,0);
                    const due = new Date(task.dueDate); due.setHours(0,0,0,0);
                    const isPastDue = due < today;
                    return (
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        padding: '1px 6px', borderRadius: 4,
                        backgroundColor: isPastDue ? '#FEE2E2' : '#FEF3C7',
                        color: isPastDue ? '#DC2626' : '#92400E',
                      }}>
                        {isPastDue ? 'OVERDUE' : 'DUE SOON'}
                      </span>
                    );
                  })()}
                  {task.scheduledDate && (
                    <span style={{
                      fontSize: 11, fontWeight: 700,
                      padding: '2px 8px', borderRadius: 4,
                      backgroundColor: '#FEF3C7', color: '#B45309',
                      border: '1px solid #FDE68A',
                      whiteSpace: 'nowrap'
                    }}>
                      📅 {task.scheduledDate}{task.scheduledTime ? ` · ${task.scheduledTime}` : ''}
                    </span>
                  )}
                </div>

                <div>
                  {task.assignedTo ? (
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      backgroundColor: getUserColor(task.assignedTo),
                      color: 'white', fontSize: 11, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }} title={task.assignedTo}>
                      {getInitials(task.assignedTo)}
                    </div>
                  ) : (
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      border: '2px dashed #CBD5E1',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <User size={12} color="#CBD5E1" />
                    </div>
                  )}
                </div>

                {!isMobile && (
                  <span style={(() => {
                    if (!task.dueDate && !task.scheduledDate) return { fontSize: 13, color: '#CBD5E1' };
                    const displayDate = task.dueDate;
                    const today = new Date(); today.setHours(0,0,0,0);
                    const due = new Date(displayDate); due.setHours(0,0,0,0);
                    const diff = Math.ceil((due - today) / (1000*60*60*24));
                    if (task.status === 'complete') return { fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 5, backgroundColor: '#F0FDF4', color: '#166534', display: 'block', textAlign: 'center' };
                    if (diff < 0) return { fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 5, backgroundColor: '#FEE2E2', color: '#DC2626', display: 'block', textAlign: 'center' };
                    if (diff <= 3) return { fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 5, backgroundColor: '#FEF3C7', color: '#92400E', display: 'block', textAlign: 'center' };
                    return { fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 5, backgroundColor: '#EFF6FF', color: '#1D4ED8', display: 'block', textAlign: 'center' };
                  })()}>
                    {task.dueDate ? task.dueDate : '—'}
                  </span>
                )}

                <span style={{
                  fontSize: 11, fontWeight: 600,
                  padding: '3px 8px', borderRadius: 6,
                  backgroundColor: task.status === 'complete' ? '#DCFCE7' : task.status === 'in-progress' ? '#FEF3C7' : '#F1F5F9',
                  color: task.status === 'complete' ? '#166534' : task.status === 'in-progress' ? '#92400E' : '#64748B',
                  textAlign: 'center',
                  display: 'block',
                }}>
                  {task.status === 'complete' ? 'Done' : task.status === 'in-progress' ? 'Active' : task.status === 'not-applicable' ? 'N/A' : 'To Do'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AccordionBoard = ({ project, onTaskClick, onToggleStatus, onToggleAll, getTaskColor, isTaskUrgent }) => {
  const isMobile = window.innerWidth < 768;
  // Group tasks by theme, sorted in correct order
  const themes = [...new Set(project.tasks.map(t => t.theme))].sort((a, b) => {
    const idxA = themeOrder.indexOf(a);
    const idxB = themeOrder.indexOf(b);
    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
  });

  // Find the first theme that has incomplete tasks — open that by default
  const firstIncomplete = themes.find(theme =>
    project.tasks.filter(t => t.theme === theme).some(t => t.status !== 'complete')
  );
  const [openSections, setOpenSections] = useState(new Set(firstIncomplete ? [firstIncomplete] : []));

  const [tollgateType, setTollgateType] = useState(null);
  const tollgateSessionKey = `tollgate-shown-${project.id}`;
  const tollgateShownThisSession = {
    get current() { return sessionStorage.getItem(tollgateSessionKey) === 'true'; },
    set current(val) { sessionStorage.setItem(tollgateSessionKey, val ? 'true' : 'false'); }
  };

  const isSeller = project.type === 'Seller';

  // detect 50% mls completion for seller deals — fires once per calendar day on task toggle
  useEffect(() => {
    if (!isSeller) return;
    if (project.tollgates?.showingStarted === true) return;
    if (project.ucDate) return;
    if (tollgateShownThisSession.current) return;
    const lastAsked = project.tollgates?.showingPromptLastAskedAt;
    if (lastAsked) {
      const lastAskedDate = new Date(lastAsked).toLocaleDateString();
      const todayDate = new Date().toLocaleDateString();
      if (lastAskedDate === todayDate) return; // already asked today
    }
    const mlsTasks = project.tasks.filter(t => (t.theme || '').toLowerCase() === 'mls');
    if (mlsTasks.length === 0) return;
    const completedMls = mlsTasks.filter(t => t.status === 'complete').length;
    const pct = completedMls / mlsTasks.length;
    if (pct >= 0.5) {
      tollgateShownThisSession.current = true;
      setTollgateType('showing-started');
    }
  }, [project.tasks, project.tollgates, project.type, isSeller]);

  // on-open checks: 72hr feedback and monday price change only (48hr re-ask handled by Cloud Function email)
  useEffect(() => {
    if (!isSeller) return;
    if (tollgateType) return; // don't stack popups
    const tg = project.tollgates || {};
    const now = new Date();
    const hoursSince = (isoString) => {
      if (!isoString) return Infinity;
      return (now - new Date(isoString)) / (1000 * 60 * 60);
    };
    // 72hr feedback reminder if showing
    if (tg.showingStarted === true && hoursSince(tg.showingFeedbackReminderLastAt) >= 72) {
      setTollgateType('feedback-reminder');
      return;
    }
    // monday price change check if showing
    if (tg.showingStarted === true) {
      const isMonday = now.getDay() === 1;
      if (isMonday && hoursSince(tg.priceChangeReminderLastAt) >= 24) {
        setTollgateType('price-change');
        return;
      }
    }
  }, [project.tollgates, project.type, tollgateType, isSeller]);

  const toggle = (theme) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(theme)) next.delete(theme);
      else next.add(theme);
      return next;
    });
  };

  // Overall progress
  const totalTasks = project.tasks.length;
  const totalComplete = project.tasks.filter(t => t.status === 'complete').length;
  const overallPct = totalTasks === 0 ? 0 : Math.round((totalComplete / totalTasks) * 100);

  return (
    <>
    <div style={{ padding: '0 0 32px' }}>
      {/* Overall progress bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '16px 4px 20px',
        borderBottom: '1px solid #E2E8F0',
        marginBottom: 16
      }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#071D39' }}>Overall Progress</span>
        <div style={{ flex: 1, height: 10, backgroundColor: '#E2E8F0', borderRadius: 5, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${overallPct}%`,
            backgroundColor: overallPct === 100 ? '#75BB2E' : overallPct > 50 ? '#75BB2E' : '#89A8B1',
            borderRadius: 5,
            transition: 'width 0.6s ease'
          }} />
        </div>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#071D39', whiteSpace: 'nowrap' }}>
          {totalComplete}/{totalTasks} ({overallPct}%)
        </span>
      </div>

      {/* Accordion sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {themes.map((theme, idx) => {
          const tasksForTheme = project.tasks
          .filter(t => t.theme === theme)
          .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
          return (
            <ThemeSection
              key={theme}
              theme={theme}
              groupNum={idx + 1}
              tasks={tasksForTheme}
              isOpen={openSections.has(theme)}
              onToggle={() => toggle(theme)}
              onTaskClick={onTaskClick}
              onToggleStatus={(task) => onToggleStatus && onToggleStatus(task)}
              onToggleAll={(allDone) => onToggleAll && onToggleAll(allDone, tasksForTheme)}
              isTaskUrgent={isTaskUrgent}
              isMobile={isMobile}
              phaseColor={getPhaseColor(theme)}
            />
          );
        })}
      </div>
    </div>

    {tollgateType && (
      <TollgateModal
        type={tollgateType}
        orgId={project.orgId}
        projectId={project.id}
        project={project}
        onClose={(nextType) => {
          setTollgateType(nextType || null);
        }}
      />
    )}
    </>
  );
};

export default AccordionBoard;
